import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toORPCError } from '@orpc/client'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ChevronDown, ChevronRight, MoreHorizontal, Plug, Plus, TriangleAlert } from 'lucide-react'
import type { McpServer } from '@hub/contracts'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SettingsPage, SettingsSection, settingsInputClass } from './settings-layout'
import { AddMcpDialog } from './add-mcp-dialog'
import { useDateLocale } from '@/i18n/dates'
import { useProjectEvent } from '@/lib/collab/presence-context'
import { useMembers, ensureMembersLoaded, membersLoaded } from '@/lib/members/members-store'
import { orpc } from '@/lib/orpc-client'
import { cn } from '@/lib/utils'

type ListResult = Awaited<ReturnType<typeof orpc.mcp.list>>

/**
 * Settings › Plugins (board wwfu): the project's remote MCP servers. Every
 * member sees the list (who authorised what, which tools HUB can reach);
 * owners and admins add, toggle, refresh, re-authorise, rename and remove.
 * The server enforces the same rule — this only hides the controls.
 */
export function PluginsSettings({ projectId }: { projectId: string }) {
  const { t } = useTranslation('settings')
  const [data, setData] = useState<ListResult | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [addOpen, setAddOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      setData(await orpc.mcp.list({ projectId }))
      setLoadError(false)
    } catch {
      setLoadError(true)
    }
  }, [projectId])

  useEffect(() => {
    void load()
  }, [load])
  useProjectEvent(['mcp:changed'], () => void load())
  useCallbackToast()

  // For "former member" next to the authorising admin.
  const members = useMembers(projectId)
  useEffect(() => {
    void ensureMembersLoaded(projectId).catch(() => undefined)
  }, [projectId])
  const memberIds = membersLoaded(projectId) ? new Set(members.map((m) => m.userId)) : null

  const canManage = data?.canManage ?? false
  const configured = data?.configured ?? true

  return (
    <SettingsPage>
      <SettingsSection title={t('nav.plugins')} description={t('plugins.intro')} className="pb-4">
        {!configured && (
          <Notice tone="warning" slot="plugins-not-configured">
            {t('plugins.notConfigured')}
          </Notice>
        )}
        {data && !canManage && <Notice slot="plugins-read-only">{t('plugins.readOnly')}</Notice>}
        {canManage && configured && (
          <div>
            <Button size="sm" data-slot="plugins-add" onClick={() => setAddOpen(true)}>
              <Plus className="size-4" />
              {t('plugins.add')}
            </Button>
          </div>
        )}
      </SettingsSection>

      <SettingsSection className="pt-4">
        {loadError && <p className="text-sm text-destructive">{t('plugins.loadFailed')}</p>}
        {data && data.servers.length === 0 && (
          <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center">
            <Plug className="mx-auto mb-2 size-5 text-muted-foreground" />
            <p className="text-sm font-medium">{t('plugins.empty')}</p>
            {canManage && configured && (
              <p className="mt-1 text-sm text-muted-foreground">{t('plugins.emptyManager')}</p>
            )}
          </div>
        )}
        {data && data.servers.length > 0 && (
          <ul className="flex flex-col gap-3" data-slot="plugins-list">
            {data.servers.map((server) => (
              <PluginRow
                key={server.id}
                projectId={projectId}
                server={server}
                canManage={canManage}
                formerMember={
                  memberIds !== null &&
                  server.authorizedBy !== null &&
                  !memberIds.has(server.authorizedBy)
                }
                onChanged={load}
              />
            ))}
          </ul>
        )}
      </SettingsSection>

      <AddMcpDialog
        projectId={projectId}
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={() => void load()}
      />
    </SettingsPage>
  )
}

function Notice({
  children,
  tone = 'info',
  slot,
}: {
  children: React.ReactNode
  tone?: 'info' | 'warning'
  slot: string
}) {
  return (
    <p
      data-slot={slot}
      className={cn(
        'flex items-start gap-2 rounded-lg border px-3 py-2 text-sm',
        tone === 'warning'
          ? 'border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200'
          : 'border-border bg-muted/40 text-muted-foreground'
      )}
    >
      {tone === 'warning' && <TriangleAlert className="mt-0.5 size-4 shrink-0" />}
      <span>{children}</span>
    </p>
  )
}

/** The OAuth callback lands on `?settings=plugins&mcpStatus=…`: toast once, then strip it. */
function useCallbackToast() {
  const { t } = useTranslation('settings')
  const search = useSearch({ strict: false }) as Record<string, unknown>
  const navigate = useNavigate()
  const status = search.mcpStatus
  const code = typeof search.mcpError === 'string' ? search.mcpError : null
  const server = typeof search.mcp === 'string' ? search.mcp : ''
  // StrictMode runs the effect twice before the URL is stripped — toast once per callback.
  const shown = useRef<string | null>(null)
  useEffect(() => {
    if (status !== 'connected' && status !== 'error') return
    const key = `${server}:${status}:${code ?? ''}`
    if (shown.current === key) return
    shown.current = key
    if (status === 'connected') toast.success(t('plugins.connected'))
    else toast.error(t('plugins.connectFailed', { code: code ?? 'error' }))
    void navigate({
      to: '.',
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        mcp: undefined,
        mcpStatus: undefined,
        mcpError: undefined,
      })) as never,
      replace: true,
      state: true as never,
    })
  }, [status, code, server, navigate, t])
}

function StatusBadge({ server }: { server: McpServer }) {
  const { t } = useTranslation('settings')
  const key = server.enabled ? server.status : 'disabled'
  const tone =
    key === 'active'
      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
      : key === 'disabled'
        ? 'border-border bg-muted text-muted-foreground'
        : key === 'error'
          ? 'border-destructive/40 bg-destructive/10 text-destructive'
          : 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200'
  return (
    <Badge variant="outline" data-status={key} className={cn('font-normal', tone)}>
      {t(`plugins.status.${key}`)}
    </Badge>
  )
}

function PluginRow({
  projectId,
  server,
  canManage,
  formerMember,
  onChanged,
}: {
  projectId: string
  server: McpServer
  canManage: boolean
  formerMember: boolean
  onChanged: () => Promise<void> | void
}) {
  const { t } = useTranslation('settings')
  const { intl } = useDateLocale()
  const [expanded, setExpanded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(intl, { year: 'numeric', month: 'short', day: 'numeric' })

  const run = async (action: () => Promise<unknown>, success?: string) => {
    setBusy(true)
    try {
      await action()
      if (success) toast.success(success)
      await onChanged()
    } catch (err) {
      toast.error(toORPCError(err).message || t('plugins.actionFailed'))
    } finally {
      setBusy(false)
    }
  }

  const ids = { projectId, serverId: server.id }
  const reauthorize = async () => {
    setBusy(true)
    try {
      const { authorizeUrl } = await orpc.mcp.startAuthorization(ids)
      window.location.assign(authorizeUrl)
    } catch (err) {
      toast.error(toORPCError(err).message || t('plugins.actionFailed'))
      setBusy(false)
    }
  }

  const usable = server.tools.filter((tool) => tool.usable).length
  const authorizer = server.authorizedByName
    ? `${server.authorizedByName}${formerMember ? ` (${t('plugins.formerMember')})` : ''}`
    : null
  const authLine =
    server.authKind === 'none'
      ? t('plugins.noAuth')
      : authorizer && server.authorizedAt
        ? t(server.authKind === 'oauth' ? 'plugins.authorizedBy' : 'plugins.apiKeyBy', {
            name: authorizer,
            date: formatDate(server.authorizedAt),
          })
        : null
  const needsAuth = server.status === 'needs_auth' || server.status === 'needs_reauth'

  return (
    <li
      data-slot="plugin-row"
      data-plugin={server.slug}
      className="rounded-lg border border-border bg-card"
    >
      <div className="flex flex-wrap items-start gap-3 p-4">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted">
          <Plug className="size-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1 basis-48 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-medium">{server.name}</p>
            <StatusBadge server={server} />
          </div>
          <p className="truncate font-mono text-xs text-muted-foreground" title={server.url}>
            {server.url}
          </p>
          {authLine && <p className="text-xs text-muted-foreground">{authLine}</p>}
          {server.authKind === 'none' && server.lastError?.includes('requires authentication') && (
            <p className="text-xs text-destructive" data-slot="plugin-requires-auth">
              {t('plugins.requiresAuth')}
            </p>
          )}
          {server.lastError && server.status !== 'active' && (
            <p className="text-xs text-destructive" data-slot="plugin-error">
              {t('plugins.lastError', { error: server.lastError })}
            </p>
          )}
          {server.lastError && server.status === 'active' && (
            <p className="text-xs text-amber-700 dark:text-amber-300" data-slot="plugin-error">
              {t('plugins.lastError', { error: server.lastError })}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canManage && needsAuth && server.authKind === 'oauth' && (
            <Button size="sm" variant="outline" disabled={busy} onClick={reauthorize}>
              {t('plugins.reauthorize')}
            </Button>
          )}
          <Switch
            aria-label={t('plugins.enabled')}
            checked={server.enabled}
            disabled={!canManage || busy}
            onCheckedChange={(enabled) => void run(() => orpc.mcp.update({ ...ids, enabled }))}
          />
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-8"
                  aria-label={t('plugins.actions', { name: server.name })}
                  disabled={busy}
                >
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() =>
                    void run(() => orpc.mcp.refreshTools(ids), t('plugins.toolsRefreshed'))
                  }
                >
                  {t('plugins.refreshTools')}
                </DropdownMenuItem>
                {server.authKind === 'oauth' && (
                  <DropdownMenuItem onSelect={() => void reauthorize()}>
                    {t('plugins.reauthorize')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
                  {t('plugins.rename')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={() => setRemoveOpen(true)}
                >
                  {t('plugins.remove')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="border-t border-border px-4 py-2">
        <button
          type="button"
          data-slot="plugin-tools-toggle"
          className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          {t('plugins.toolCount', { count: usable })}
          {server.lastUsedAt && (
            <span className="ml-2 font-normal">
              · {t('plugins.lastUsed', { date: formatDate(server.lastUsedAt) })}
            </span>
          )}
        </button>
        {expanded && (
          <ul className="mt-2 space-y-1.5 pb-1" data-slot="plugin-tools">
            {server.tools.length === 0 && (
              <li className="text-xs text-muted-foreground">{t('plugins.noTools')}</li>
            )}
            {server.tools.map((tool) => (
              <li key={tool.name} className="text-xs">
                <span className={cn('font-mono', !tool.usable && 'line-through opacity-60')}>
                  {tool.name}
                </span>
                {!tool.usable && (
                  <span className="ml-2 text-amber-700 dark:text-amber-300">
                    {t('plugins.toolUnavailable')}
                  </span>
                )}
                {tool.description && (
                  <span className="text-muted-foreground"> — {tool.description}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <RenameDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        initial={server.name}
        onSave={(name) => run(() => orpc.mcp.update({ ...ids, name }))}
      />
      <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('plugins.removeTitle', { name: server.name })}</AlertDialogTitle>
            <AlertDialogDescription>{t('plugins.removeDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('plugins.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => void run(() => orpc.mcp.remove(ids), t('plugins.removed'))}
            >
              {t('plugins.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  )
}

function RenameDialog({
  open,
  onOpenChange,
  initial,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  initial: string
  onSave: (name: string) => Promise<void>
}) {
  const { t } = useTranslation('settings')
  const [name, setName] = useState(initial)
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    if (open) setName(initial)
  }, [open, initial])
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    await onSave(name.trim())
    setSaving(false)
    onOpenChange(false)
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('plugins.renameTitle')}</DialogTitle>
            <DialogDescription className="sr-only">{t('plugins.renameTitle')}</DialogDescription>
          </DialogHeader>
          <input
            className={settingsInputClass}
            value={name}
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('plugins.cancel')}
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? t('plugins.saving') : t('plugins.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
